/**
 * BookshopInvitesCard — iter-435g Phase C follow-up.
 *
 * Shows pending team invites next to active members on the Bookshop team
 * page, with:
 *   - Status chips (pending / expired / accepted / revoked)
 *   - "Resend email" quick action (re-fires the same token via SMTP)
 *   - "Revoke" quick action
 *   - Invite dialog with email + role
 *
 * Backend: /api/bookshop/team/invite, /api/bookshop/team/invites,
 *          /api/bookshop/team/invites/{id} (DELETE).
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Mail, Send, X, RefreshCw, Copy } from 'lucide-react';
import { getToken } from '@/lib/authToken';

const API = process.env.REACT_APP_BACKEND_URL;
const authH = () => ({ Authorization: `Bearer ${getToken()}` });

const STATUS_STYLES = {
  pending:  { cls: 'border-amber-400/40 text-amber-300 bg-amber-500/10' },
  accepted: { cls: 'border-emerald-400/40 text-emerald-300 bg-emerald-500/10' },
  revoked:  { cls: 'border-slate-600 text-slate-400' },
  expired:  { cls: 'border-rose-400/40 text-rose-300 bg-rose-500/10' },
};


export function BookshopInvitesCard({ roles }) {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [showInvite, setShowInvite] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const r = await axios.get(`${API}/api/bookshop/team/invites`, { headers: authH() });
      setRows(r.data.invites || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load invites');
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  const resend = async (row) => {
    // Re-POST /invite with the same email + role. Backend returns the
    // existing token (via `invite_already_pending` short-circuit) and
    // dispatches a fresh email.
    try {
      const r = await axios.post(`${API}/api/bookshop/team/invite`,
        { email: row.email, role: row.role }, { headers: authH() });
      const sent = r.data?.email_dispatch?.sent;
      toast.success(sent ? 'Invite email resent' : 'Token re-issued (SMTP disabled — share it manually)');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Resend failed');
    }
  };

  const revoke = async (row) => {
    if (!confirm(`Revoke invite to ${row.email}?`)) return;
    try {
      await axios.delete(`${API}/api/bookshop/team/invites/${row.id}`, { headers: authH() });
      toast.success('Invite revoked');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Revoke failed');
    }
  };

  const pendingCount = rows.filter(r => r.status === 'pending').length;

  return (
    <Card className="bg-slate-900/60 border-slate-800"
            data-testid="bookshop-invites-card">
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="text-sm inline-flex items-center gap-2">
            <Mail className="h-4 w-4 text-amber-300" /> Team invites
            {pendingCount > 0 && (
              <Badge variant="outline"
                        className="border-amber-400/40 text-amber-300 bg-amber-500/10 text-[10px]"
                        data-testid="invites-pending-count">
                {pendingCount} pending
              </Badge>
            )}
          </CardTitle>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={load}
                    className="border-slate-700 text-slate-300 hover:bg-slate-800"
                    data-testid="invites-refresh">
            <RefreshCw className={`h-3 w-3 mr-1 ${busy ? 'animate-spin' : ''}`} />Refresh
          </Button>
          <Dialog open={showInvite} onOpenChange={setShowInvite}>
            <DialogTrigger asChild>
              <Button size="sm" data-testid="invites-new-btn">
                <Send className="h-4 w-4 mr-1" />Send invite
              </Button>
            </DialogTrigger>
            <InviteDialog roles={roles}
                             onClose={() => setShowInvite(false)}
                             onSent={() => { setShowInvite(false); load(); }} />
          </Dialog>
        </div>
      </CardHeader>
      <CardContent className="space-y-1">
        {!busy && !rows.length && (
          <div className="text-sm text-slate-500 py-6 text-center">
            No invites sent yet. Click "Send invite" to add a team member by email.
          </div>
        )}
        {rows.map(row => {
          const st = STATUS_STYLES[row.status] || STATUS_STYLES.pending;
          return (
            <div key={row.id}
                  className="border border-slate-800 rounded-lg p-2 flex items-center gap-3 bg-slate-800/30 text-xs"
                  data-testid={`invite-row-${row.id}`}>
              <div className="flex-1 min-w-0">
                <div className="text-slate-100 truncate">{row.email}</div>
                <div className="text-slate-500 text-[10px]">
                  {row.role.replace('_', ' ')} · invited by {row.invited_by_email || '—'} ·{' '}
                  {row.invited_at ? new Date(row.invited_at).toLocaleString() : ''}
                </div>
              </div>
              <Badge variant="outline" className={`text-[10px] ${st.cls}`}
                        data-testid={`invite-status-${row.id}`}>
                {row.status}
              </Badge>
              {row.status === 'pending' && (
                <>
                  <Button size="sm" variant="outline"
                            className="h-7 border-slate-700 text-slate-300 hover:bg-slate-800"
                            onClick={() => resend(row)}
                            data-testid={`invite-resend-${row.id}`}>
                    <Send className="h-3 w-3 mr-1" />Resend
                  </Button>
                  <Button size="sm" variant="outline"
                            className="h-7 border-slate-700 text-slate-300 hover:bg-slate-800"
                            onClick={() => revoke(row)}
                            data-testid={`invite-revoke-${row.id}`}>
                    <X className="h-3 w-3" />
                  </Button>
                </>
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}


function InviteDialog({ roles, onClose, onSent }) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState(
    roles.find(r => r.is_default)?.value || roles[0]?.value || 'bookshop_editor'
  );
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async () => {
    if (!email.trim()) { toast.error('Email required'); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/api/bookshop/team/invite`,
        { email: email.trim(), role }, { headers: authH() });
      setResult(r.data);
      const sent = r.data?.email_dispatch?.sent;
      toast.success(sent ? 'Invite email sent' : 'Token created (SMTP off — share it manually)');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Invite failed');
    } finally { setBusy(false); }
  };

  const copyToken = () => {
    navigator.clipboard?.writeText(result?.token || '');
    toast.success('Token copied');
  };

  return (
    <DialogContent className="bg-slate-950 border-slate-800 text-slate-200">
      <DialogHeader><DialogTitle>Send bookshop team invite</DialogTitle></DialogHeader>
      {!result ? (
        <>
          <div className="space-y-3">
            <div>
              <Label className="text-xs text-slate-400">Email</Label>
              <Input value={email} onChange={e => setEmail(e.target.value)}
                      placeholder="teammate@example.com"
                      className="bg-slate-900 border-slate-800"
                      data-testid="invite-email" />
            </div>
            <div>
              <Label className="text-xs text-slate-400">Role</Label>
              <Select value={role} onValueChange={setRole}>
                <SelectTrigger className="bg-slate-900 border-slate-800"
                                    data-testid="invite-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {roles.map(r => (
                    <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <p className="text-[10px] text-slate-500">
              Invite expires in 7 days. If SMTP is off you can still share the token from the resend action.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={onClose}
                      className="border-slate-700 text-slate-300 hover:bg-slate-800"
                      data-testid="invite-cancel">Cancel</Button>
            <Button onClick={submit} disabled={busy}
                      data-testid="invite-submit">
              {busy ? 'Sending…' : 'Send invite'}
            </Button>
          </DialogFooter>
        </>
      ) : (
        <div className="space-y-3 text-sm">
          <div className="text-emerald-300">✔ Invite created</div>
          <div className="text-slate-400">
            SMTP dispatch:{' '}
            {result.email_dispatch?.sent
              ? <span className="text-emerald-300">delivered</span>
              : <span className="text-amber-300">off — share the token manually</span>}
          </div>
          <div className="text-[10px] uppercase text-slate-500">Token</div>
          <code className="block text-[11px] bg-slate-900 border border-slate-800 rounded p-2 break-all"
                  data-testid="invite-token-copy">
            {result.token}
          </code>
          <DialogFooter>
            <Button variant="outline" onClick={copyToken}
                      className="border-slate-700 text-slate-300 hover:bg-slate-800">
              <Copy className="h-3 w-3 mr-1" />Copy token
            </Button>
            <Button onClick={() => { setResult(null); setEmail(''); onSent(); }}
                      data-testid="invite-done">Done</Button>
          </DialogFooter>
        </div>
      )}
    </DialogContent>
  );
}

export default BookshopInvitesCard;
